import { APIClient, HTTPClient, HTTPClientCaches, HTTPClientConfig, HTTPClientNetwork } from '@innerhive-internal/innerhive-api-client';
import { AnonymousViewerSessionManager } from './AnonymousViewerSessionManager';
import { ICareMap, ICareMapKey } from '../../caremap/ICareMap';
import jwtDecode from 'jwt-decode';
import { AsyncStorageProvider, IAsyncSmartStorageProvider } from '../../support/AsyncStorageProvider';
import { nanoid } from 'nanoid';

const CAREMAP_MEDIA_TYPE = 'application/vnd.innerhive.caremap.v3+json';

type IEpochSeconds = number;

interface IBasicCaremapInfoForError {
    patientName: string;
}

interface ICompositeAnonymousHttpClientStorageState {
    sessionToBasicCaremapInfo: {
        [sessionKey: string]: IBasicCaremapInfoForError
    }
    anonymousUserId: string;
}

interface IClientConfig {
    client: HTTPClient;
    session_token: string;
    access_token: string;
    careMap?: ICareMap;
}

interface IUrlPrefixToClient {
    [urlPrefix: string]: IClientConfig;
}

interface ISessionClients {
    urlPrefixToClient: IUrlPrefixToClient;
    defaultClient?: HTTPClient;
}

export const VIEWER_JWT_EXPIRED = 'viewer.jwt.expired';

export class ViewerJWTExpiredError extends Error {
    constructor(careMaps: IBasicCaremapInfoForError[]){
        super('one or more sessions tokens were expired');
        this.expiredCareMaps = careMaps;
    }
    errorCode = VIEWER_JWT_EXPIRED;
    expiredCareMaps?: IBasicCaremapInfoForError[];
}

export class AnonymousClientStorageProvider {
    storage: IAsyncSmartStorageProvider<ICompositeAnonymousHttpClientStorageState>;

    static inject = () => [AsyncStorageProvider];
    constructor(provider: AsyncStorageProvider) {
        this.storage = provider.getJSONProvider<ICompositeAnonymousHttpClientStorageState>('CompositeAnonymousHttpClient', { envNeutral: false, userNeutral: true });
    }
}

export class CompositeAnonymousHttpClient {
    static inject = () => [
        HTTPClientConfig,
        AnonymousViewerSessionManager,
        AnonymousClientStorageProvider,
        HTTPClientNetwork,
        HTTPClientCaches
    ];

    constructor(
        private config: HTTPClientConfig,
        private anonymousViewerSessionManager: AnonymousViewerSessionManager,
        private storageProvider: AnonymousClientStorageProvider,
        private network: HTTPClientNetwork,
        private caches: HTTPClientCaches
    ){}

    private sessionToBasicCaremapInfo!: ICompositeAnonymousHttpClientStorageState['sessionToBasicCaremapInfo'];

    public anonymousUserId!: string; // set in init

    private urlPrefixToClient: IUrlPrefixToClient = {};

    private defaultClient?: HTTPClient;

    // Reused across all session-exchange calls so APIClient's per-instance entry cache
    // avoids a redundant GET / for each session token in the init loop.
    private anonymousApiClient?: APIClient;

    partiallyFailedError?: ViewerJWTExpiredError;

    // The care map whose path the url falls under, otherwise the session that has no care map ('*').
    // Undefined leaves the choice to the default client.
    private findSessionPrefixForUrl = (url: string): string | undefined => {
        const prefixes = Object.keys(this.urlPrefixToClient);
        return prefixes.find(prefix => url.startsWith(prefix)) ?? prefixes.find(prefix => prefix === '*');
    };

    private getSessionConfigForUrl = (url: string): IClientConfig => {
        const matchingPrefix = this.findSessionPrefixForUrl(url);
        if (matchingPrefix) {
            return this.urlPrefixToClient[matchingPrefix];
        }

        const defaultConfig = Object.values(this.urlPrefixToClient).find(config => config.client === this.defaultClient);
        if (!defaultConfig) {
            throw new Error('we no longer have a default client, the user needs to restart, which will redirect them to login');
        }
        return defaultConfig;
    };

    private getHttpClientForUrl = (url: string): HTTPClient => this.getSessionConfigForUrl(url).client;

    // For a caller that must act as the session serving a url rather than through this facade, which
    // routes each request by its own url: a media token minted at '/media-tokens' would otherwise
    // belong to whichever session '*' or the default resolves to, not the care map being shown.
    getSessionServingUrl = (url: string): { sessionToken: string; client: HTTPClient } => {
        const { session_token, client } = this.getSessionConfigForUrl(url);
        return { sessionToken: session_token, client };
    };

    private cleanUpDeadHttpClient = async (deadClient: HTTPClient) => {
        const prefixes = Object.keys(this.urlPrefixToClient);
        const matchingPrefix = prefixes.find(p => this.urlPrefixToClient[p].client === deadClient);
        if (!matchingPrefix) {
            // Already removed, or replaced by a recovery.
            return;
        }

        const deadSessionToken = this.urlPrefixToClient[matchingPrefix].session_token;
        await this.anonymousViewerSessionManager.removeSession(deadSessionToken);

        delete this.urlPrefixToClient[matchingPrefix];
        if (this.defaultClient === deadClient){
            this.defaultClient = undefined;
            if (Object.keys(this.urlPrefixToClient).length > 0){
                const firstPrefix = Object.keys(this.urlPrefixToClient)[0];
                this.defaultClient = this.urlPrefixToClient[firstPrefix].client;
            }
        }
    };

    getInitCaremaps = (): ICareMap[] => {
        return Object.keys(this.urlPrefixToClient).map(prefix => this.urlPrefixToClient[prefix].careMap).filter(Boolean) as ICareMap[];
    };

    getCaremapForSession = (session_token: string) => {
        const prefixes = Object.keys(this.urlPrefixToClient);
        const matchingPrefix = prefixes.find(p => this.urlPrefixToClient[p].session_token === session_token)!;
        if (!matchingPrefix){
            throw new Error(`no matching prefix for ${session_token}. which should not be possible`);
        }
        return this.urlPrefixToClient[matchingPrefix].careMap;
    };

    init = async () => {
        const fromStorage = await this.storageProvider.storage.get();
        this.sessionToBasicCaremapInfo = fromStorage?.sessionToBasicCaremapInfo || {};
        this.anonymousUserId = fromStorage?.anonymousUserId || `anonymous-${nanoid()}`;

        if (this.network && !this.network.isOnline) {
            const client = new HTTPClient(this.config, this.network, this.caches);
            if (!await client.isCached('/')) {
                // minimal caches required for app to start offline as anonymous user
                // (these need to be kept in sync with api responses)
                await client.cache('/', { user: { id: 'anonymous-default', sub: this.anonymousUserId, tokenType: 'caremap-viewer' }, links: { resources: { href: '/resources' }, sessionExchange: { href: '/sessions/[[session_token]]?anonymousUserId=[[anonymousUserId]]' } } });
                await client.cache(`/sessions/default?anonymousUserId=${this.anonymousUserId}`, { access_token: 'dummy-offline-token' });
            }
        }

        const failedTokens: string[] = [];

        // Built aside and swapped in once complete, so a request arriving during a recovery is
        // served by the outgoing clients rather than finding none.
        const sessionClients: ISessionClients = { urlPrefixToClient: {} };

        for (const session_token of this.anonymousViewerSessionManager.getSessions().slice(0)){
            try {
                await this.addSessionClient({ sessionClients, session_token });
            }
            catch(err: any){
                if (err && err.errorCode === VIEWER_JWT_EXPIRED){
                    this.anonymousViewerSessionManager.removeSession(session_token);
                    failedTokens.push(session_token);

                } else {
                    throw err;
                }
            }
        }

        this.urlPrefixToClient = sessionClients.urlPrefixToClient;
        this.defaultClient = sessionClients.defaultClient;

        await this.cacheKnownCaremapsForFuturePageLoads();

        if (failedTokens.length > 0){
            const error = new ViewerJWTExpiredError(failedTokens.map(session_token => this.sessionToBasicCaremapInfo[session_token]).filter(info => Boolean(info)));
            if (this.getInitCaremaps().length > 0){
                this.partiallyFailedError = error;
            } else {
                throw error;
            }
        }
    };

    private addSessionClient = async ({ sessionClients, session_token }: { sessionClients: ISessionClients; session_token: string; }) => {
        const access_token = await this.getAccessToken(session_token);

        const careMap = await this.getCareMap({ access_token });

        let previousSessionConfig: IClientConfig | undefined;
        if (careMap) {
            previousSessionConfig = this.findSessionConfigForCareMap({ urlPrefixToClient: sessionClients.urlPrefixToClient, careMapKey: careMap });
        } else {
            previousSessionConfig = sessionClients.urlPrefixToClient['*'];
        }

        if (previousSessionConfig) {
            const previousSessionToken = previousSessionConfig.session_token;

            const thisOneExpiresLater = this.getAccessTokenExpiration(access_token) > this.getAccessTokenExpiration(previousSessionConfig.access_token);

            if (thisOneExpiresLater){
                this.anonymousViewerSessionManager.replaceSessionForSameCaremap({
                    older: previousSessionToken,
                    newer: session_token
                });
                // when we add this one, it will stomp on the old one
                // b/c they have the same prefix
                this.addClient({ sessionClients, access_token, session_token, careMap });
            }
            else {
                this.anonymousViewerSessionManager.replaceSessionForSameCaremap({
                    older: session_token,
                    newer: previousSessionToken
                });
                // we don't need to change anything here, b/c the prefix is already registered
            }
        }
        else {
            this.addClient({ sessionClients, access_token, careMap, session_token });
        }
    };

    private recovery?: Promise<void>;

    // A 401 can happen if we reconnect after generating a dummy offline token, in which case we need
    // to reinitialize, and the caller retries its request once. One recovery runs at a time, and a
    // client already replaced by a finished recovery needs none.
    recoverFromUnauthorized = async (unauthorizedClient: HTTPClient) => {
        if (!this.recovery) {
            const isCurrent = Object.values(this.urlPrefixToClient).some(config => config.client === unauthorizedClient);
            if (!isCurrent) {
                return;
            }
            this.recovery = this.init().finally(() => {
                this.recovery = undefined;
            });
        }
        await this.recovery;
    };

    private cacheKnownCaremapsForFuturePageLoads = async () => {

        for (const prefix of Object.keys(this.urlPrefixToClient)){
            const { session_token, careMap } = this.urlPrefixToClient[prefix];
            if (careMap) {
                this.sessionToBasicCaremapInfo[session_token] = {
                    patientName: this.getPatientName(careMap)
                };
            }
        }

        await this.storageProvider.storage.set({
            sessionToBasicCaremapInfo: this.sessionToBasicCaremapInfo,
            anonymousUserId: this.anonymousUserId
        });
    };

    private getPatientName = (careMap: ICareMap) => {
        const patient = careMap.patient;
        const first = patient.firstName || '';
        const last = patient.lastName || '';
        let name = first;
        if (last){
            const space = first ? ' ' : '';
            name = `${first}${space}${last}`;
        }
        return name;
    };

    getSessionConfigForCareMap = (careMapKey: ICareMapKey) => {
        return this.findSessionConfigForCareMap({ urlPrefixToClient: this.urlPrefixToClient, careMapKey });
    };

    private findSessionConfigForCareMap = ({ urlPrefixToClient, careMapKey }: { urlPrefixToClient: IUrlPrefixToClient; careMapKey: ICareMapKey; }) => {
        const prefixes = Object.keys(urlPrefixToClient);
        const matchingPrefix = prefixes.find(p => urlPrefixToClient[p].careMap?.userId === careMapKey.userId && urlPrefixToClient[p].careMap?.careMapId === careMapKey.careMapId);
        if (!matchingPrefix) {
            return undefined;
        }
        return urlPrefixToClient[matchingPrefix];
    };

    getAccessTokenExpirationForCaremap = (careMapKey: ICareMapKey) => {
        const matchingConfig = this.getSessionConfigForCareMap(careMapKey);
        if (!matchingConfig){
            throw new Error(`no config yet for ${JSON.stringify(careMapKey)}`);
        }
        const access_token = matchingConfig.access_token;

        return this.getAccessTokenExpiration(access_token);
    };

    private getAccessTokenExpiration = (access_token: string): IEpochSeconds => {
        const decoded = jwtDecode(access_token) as { exp?: IEpochSeconds };

        return decoded.exp || Number.MAX_VALUE;
    };

    getSessionTokenByCaremap = (careMapKey: ICareMapKey) => {
        const matchingConfig = this.getSessionConfigForCareMap(careMapKey);
        if (!matchingConfig){
            throw new Error(`no config yet for ${JSON.stringify(careMapKey)}`);
        }
        return matchingConfig.session_token;
    };

    private addClient = ({ sessionClients, access_token, session_token, careMap }: { sessionClients: ISessionClients; access_token: string; session_token: string; careMap?: ICareMap; }) => {
        const client = new HTTPClient(
            { ...this.config, accessToken: access_token },
            this.network,
            this.caches
        );

        const urlPrefix = (careMap ? `/users/${careMap.userId}/caremaps/${careMap.careMapId}` : '*');

        sessionClients.urlPrefixToClient[urlPrefix] = {
            client,
            session_token,
            access_token,
            careMap
        };

        // want the last (most recent) client to be the default client
        // so that it is what is used for entry resource on app start
        // and so we can quickly surface that it is expired (if it is)
        sessionClients.defaultClient = client;
    };

    onRequesting = (...params: any) => {
        // TODO: we could wire them all up, but I don't think we use this for anonynous so YAGNI
        throw new Error('not supported');
    };
    request = async ({ url, method, data, headers, options }: any) => {
        const httpClient = this.getHttpClientForUrl(url);
        try {
            return await httpClient.request({ url, method, data, headers, options });
        }
        catch (err: any){
            if (err && err.errorCode === VIEWER_JWT_EXPIRED){
                // then this sessipn / token is dead and we need to remove it
                await this.cleanUpDeadHttpClient(httpClient);
                throw err;
            } else if (err && err.status === 401) {
                await this.recoverFromUnauthorized(httpClient);
                const newClient = this.getHttpClientForUrl(url);
                return await newClient.request({ url, method, data, headers, options });
            } else {
                throw err;
            }
        }
    };

    delete = (url: string, headers?: any) => this.request({ url, method: 'DELETE', headers });
    patch = (url: string, data?: any, headers?: any) => this.request({ url, method: 'PATCH', data, headers });
    post = (url: string, data?: any, headers?: any) => this.request({ url, method: 'POST', data, headers });
    put = (url: string, data?: any, headers?: any) => this.request({ url, method: 'PUT', data, headers });
    get = (url: string, headers?: any, options?: any) => this.request({ url, method: 'GET', headers, options });


    private getAccessToken = async (session_token: string): Promise<string> => {
        if (!this.anonymousApiClient) {
            this.anonymousApiClient = new APIClient(new HTTPClient(this.config, this.network, this.caches));
        }
        const apiClient = this.anonymousApiClient;

        try {
            const entry = await apiClient.entry();
            const sessionExchange = entry?.links?.sessionExchange;
            if (!sessionExchange) {
                throw new Error('sessionExchange link missing from anonymous entry');
            }
            const response = await apiClient.get(sessionExchange, undefined, {
                session_token,
                anonymousUserId: this.anonymousUserId
            });
            return response.access_token;
        }
        catch (err: any){
            // as well (since it also converts sessions)
            // it will be redundant, but since this is both old ui and new ui, easier to never use
            // CompositeAnonymousHttpClient in the old UI and accept this duplication for now (IMO)
            if (err.status === 404){
                // it may not have ever existed, but that should only be true for a malicious user
                // so lets assume it did exist but is now expired
                err.errorCode = VIEWER_JWT_EXPIRED;
            }
            throw err;
        }

    };

    private getCareMap = async ({ session_token, access_token }: { session_token?: string; access_token?: string; }) => {
        if (!access_token && !session_token){
            throw new Error('one of session_token, access_token is required');
        }

        if (session_token && !access_token){
            access_token = await this.getAccessToken(session_token);
        }

        const client = new HTTPClient(
            { ...this.config, accessToken: access_token },
            this.network,
            this.caches
        );
        const api = new APIClient(client);

        const entry = await api.entry();

        if (!entry.links.careMaps) {
            return undefined;
        }

        const caremaps = await api.get(entry.links.careMaps, CAREMAP_MEDIA_TYPE);
        return caremaps[0] as (ICareMap & { links: { promoteFromAnonymous: any }});
    };
}
